import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  LinearProgress,
  styled,
  Typography,
  Zoom,
} from '@mui/material'
import { FormikHelpers, FormikProvider, FormikValues, useFormik } from 'formik'
import { t } from 'i18next'
import { useConfirm } from 'material-ui-confirm'
import { FC, ReactNode, useCallback, useMemo } from 'react'
import { VinciButton } from '../VinciButton'

interface IForm {
  initialValues: FormikValues
  values?: FormikValues
  onSubmit: (values: FormikValues, formikHelper: FormikHelpers<any>) => void | Promise<void>
  validationSchema?: any
  validateOnChange?: boolean
  validateOnBlur?: boolean
  validateOnMount?: boolean
  enableReinitialize?: boolean
  initialValid?: boolean
}

interface ISimpleFormModalProps {
  form: IForm
  open: boolean
  title?: string
  children: ReactNode
  deleteButton?: boolean
  onDelete?: (id: string) => Promise<void>
  onClose: () => void
  blurBackground?: boolean
  actionsSlot?: ReactNode | null
  resetFormStateAfterSubmit?: boolean
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
  },
}))

export const SimpleFormModal: FC<ISimpleFormModalProps> = ({
  form,
  open,
  title = '',
  children,
  deleteButton,
  onDelete,
  onClose,
  blurBackground = false,
  actionsSlot = null,
  resetFormStateAfterSubmit = true,
}) => {
  const confirm = useConfirm()

  const onSubmit = useMemo(() => form.onSubmit, [form.onSubmit])

  const handleSubmit = useCallback(
    async (values: FormikValues, formikHelper: FormikHelpers<FormikValues>) => {
      await onSubmit(values, formikHelper)

      if (resetFormStateAfterSubmit) {
        formikHelper.resetForm()
      }
    },
    [onSubmit, resetFormStateAfterSubmit],
  )

  const formik = useFormik({
    ...form,
    onSubmit: handleSubmit,
  })

  const handleClose = useCallback(() => {
    if (!formik.dirty) return onClose()

    confirm({
      description: 'There are unsaved changes. Are you sure you want to exit?',
    }).then(onClose)
  }, [formik.dirty, confirm, onClose])

  const handleDelete = useCallback(() => {
    if (!onDelete) return

    confirm({
      description: 'Are you sure you want to delete?',
    }).then(() => {
      onDelete(form.values?._id ?? '')
    })
  }, [confirm, form.values, onDelete])

  const canSave = useMemo(() => {
    if (form.initialValid && !formik.dirty) return true
    if (!formik.dirty) return false
    if (!formik.isValid) return false

    return true
  }, [formik.dirty, formik.isValid, form.initialValid])

  return (
    <BootstrapDialog
      open={open}
      onClose={handleClose}
      sx={{
        backdropFilter: blurBackground ? 'blur(1px)' : undefined,
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        id='customized-dialog-title'
      >
        <Typography variant='subtitle1' textAlign={'center'}>
          {t(title)}
        </Typography>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <FormikProvider value={formik}>
        <DialogContent
          dividers
          sx={{
            minWidth: 400,
            minHeight: 120,
            maxWidth: '100vw !important',
          }}
        >
          {children}
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            p: 30,
            height: 'unset',
          }}
        >
          {actionsSlot}
          {deleteButton && (
            <VinciButton
              variant='text'
              color='error'
              onClick={handleDelete}
              sx={{
                marginRight: 'auto',
              }}
            >
              {t('Delete')}
            </VinciButton>
          )}
          <VinciButton
            variant='text'
            color='error'
            onClick={handleClose}
            disabled={formik.isSubmitting}
          >
            {t('Cancel')}
          </VinciButton>
          <VinciButton
            type='submit'
            onClick={() => formik.submitForm()}
            disabled={!canSave || formik.isSubmitting}
          >
            {t('Save')}
          </VinciButton>
        </DialogActions>
      </FormikProvider>
      <Zoom in={formik.isSubmitting}>
        <LinearProgress sx={{ width: '100%' }} />
      </Zoom>
    </BootstrapDialog>
  )
}
